import { useMemo } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useUrlParams } from '../hooks/useUrlState';
import { api, AuditEntry } from '../lib/api';
import { SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge, Spinner, EmptyState, FilterBar, Select, ActionButton, SortableTh } from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';

const ACTION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All actions' },
  { value: 'user_updated', label: 'User updated' },
  { value: 'period_locked', label: 'Period locked' },
  { value: 'impersonation_started', label: 'Impersonation started' },
  { value: 'impersonation_ended', label: 'Impersonation ended' },
  { value: 'refresh_triggered', label: 'Refresh triggered' },
];

function actionBadge(action: string) {
  const map: Record<string, 'green' | 'amber' | 'blue' | 'red' | 'gray'> = {
    user_updated: 'blue',
    period_locked: 'red',
    impersonation_started: 'amber',
    impersonation_ended: 'gray',
    refresh_triggered: 'green',
  };
  return <Badge variant={map[action] ?? 'gray'}>{action.replace(/_/g, ' ')}</Badge>;
}

function formatTs(ts: string) {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function DetailCell({ entry }: { entry: AuditEntry }) {
  const { action, target_display_name, entity_id, detail } = entry;

  if (action === 'user_updated') {
    const patch = (detail ?? {}) as Record<string, unknown>;
    const pieces = Object.entries(patch)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => `${k} → ${String(v)}`)
      .join(', ');
    return <span>{target_display_name ?? entity_id}: {pieces || '(no-op)'}</span>;
  }
  if (action === 'period_locked') {
    const lockedCount = (detail as Record<string, unknown> | null)?.locked_count;
    return <span>{entity_id}{typeof lockedCount === 'number' ? ` (${lockedCount} records)` : ''}</span>;
  }
  if (action === 'impersonation_started') {
    const role = (detail as Record<string, unknown> | null)?.target_role;
    return <span>As {target_display_name ?? entity_id}{role ? ` (${role})` : ''}</span>;
  }
  if (action === 'impersonation_ended') return <span>—</span>;
  if (action === 'refresh_triggered') {
    const trig = (detail as Record<string, unknown> | null)?.triggered_by ?? 'manual';
    return <span>{String(trig)}</span>;
  }
  return <span style={{ color: 'var(--color-text-muted)' }}>{JSON.stringify(detail ?? {})}</span>;
}

export default function AdminAuditPage() {
  const [params, patchParams] = useUrlParams({ action: '', limit: '100' });
  const actionFilter = params.action;
  const limit = parseInt(params.limit) || 100;

  const query = useMemo(
    () => ({ action: actionFilter || undefined, limit }),
    [actionFilter, limit],
  );

  const { data, isLoading, error, refetch } = useQuery(
    () => api.admin.audit(query),
    [query],
  );

  const entries = data?.entries ?? [];
  const { sorted: sortedEntries, sort } = useTableSort(
    entries,
    {
      when:   (e) => e.created_at,
      actor:  (e) => e.actor_display_name ?? e.actor_id ?? '',
      action: (e) => e.action,
    },
    { key: 'when', dir: 'desc' },
  );

  return (
    <>
      <SectionLabel>Audit log</SectionLabel>
      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Recent activity</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
          </span>
          <div style={{ marginLeft: 'auto' }}>
            <ActionButton onClick={refetch} variant="default">Refresh</ActionButton>
          </div>
        </CardHeader>

        <FilterBar>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Action</span>
          <Select value={actionFilter} onChange={(v) => patchParams({ action: v })}>
            {ACTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 12 }}>Show</span>
          <Select value={String(limit)} onChange={(v) => patchParams({ limit: v })}>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="250">250</option>
            <option value="500">500</option>
          </Select>
        </FilterBar>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <EmptyState message={error} />
        ) : entries.length === 0 ? (
          <EmptyState message="No audit entries match these filters" />
        ) : (
          <Table>
            <thead>
              <tr>
                <SortableTh columnKey="when"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>When</SortableTh>
                <SortableTh columnKey="actor"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Actor</SortableTh>
                <SortableTh columnKey="action" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Action</SortableTh>
                <Th>Details</Th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map((e) => (
                <Tr key={e.id}>
                  <Td muted style={{ whiteSpace: 'nowrap', fontFamily: 'var(--font-mono, monospace)', fontSize: 11 }}>
                    {formatTs(e.created_at)}
                  </Td>
                  <Td>{e.actor_display_name ?? e.actor_id ?? 'system'}</Td>
                  <Td>{actionBadge(e.action)}</Td>
                  <Td><DetailCell entry={e} /></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
